import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { UnreachableCaseError } from "ts-essentials";

import { InvalidOperationException } from "../exceptions/exceptions";
import {
  CustomQueryParamsHandling,
  getPagePath,
  Page,
} from "../routes/shared/shared-route.constant";
import { Product } from "../store/shared/product";

import { LanguageService } from "./language/language.service";

@Injectable({
  providedIn: "root",
})
export class UrlService {
  private readonly _window: Window;

  constructor(
    private readonly _languageService: LanguageService,
    private readonly _router: Router,
    @Inject(DOCUMENT) private _document: Document,
  ) {
    if (!this._document.defaultView) {
      throw new InvalidOperationException(
        "Was not able to find a global window object. This could be a side effect when changing to service side rendering (SSR)",
      );
    }

    this._window = this._document.defaultView;
  }

  /**
   * Create an absolute url with a product and a page.
   */
  createAbsoluteUrlByPage(
    product: Product,
    page: Page,
    customQueryParamsHandling: CustomQueryParamsHandling,
    newUrlSearchParams?: URLSearchParams,
  ) {
    const pagePath = getPagePath(product, page);

    const registeredRoute = this._router.config.find(
      (registeredRoute) => registeredRoute.path === pagePath,
    );

    if (!registeredRoute) {
      throw new InvalidOperationException(
        `Was not able to find a route for the page '${page}'. This should not be possible and would indicate that the 'LifeInsurancePagePath' enum value is currently not being used as part of a route.`,
      );
    }

    const urlSearchParams = this.createQueryString(
      this._window.location.search,
      customQueryParamsHandling,
      newUrlSearchParams,
    );

    const originWithLanguagePath = this.getBaseUrl();

    if (!registeredRoute.path) {
      throw new InvalidOperationException(
        `Could not find a path for the registered route for path '${page.toString()}. This exception should never occur.'`,
      );
    }

    return new URL(
      `${originWithLanguagePath.toString()}/${registeredRoute.path.toString()}?${urlSearchParams}`,
    );
  }

  /**
   * Create an absolute url with a language specific base url.
   *
   * Example: http://localhost:4200/de + /term-life-insurance/gender?quote=1345
   */
  createAbsoluteUrlByRelativePath(relativeUrl: string) {
    const baseUrl = this.getBaseUrl();
    return new URL(relativeUrl, baseUrl);
  }

  /**
   * Returns the domain name with the language suffix (e.g http://localhost:4200/de)
   */
  private getBaseUrl() {
    const language = this._languageService.getLanguage();
    return new URL(`${this._window.location.origin}/${language}`);
  }

  private createQueryString(
    existingQueryString: string,
    customQueryParamsHandling: CustomQueryParamsHandling,
    newUrlSearchParams?: URLSearchParams,
  ) {
    switch (customQueryParamsHandling) {
      case CustomQueryParamsHandling.PRESERVE: {
        return new URLSearchParams(existingQueryString);
      }
      case CustomQueryParamsHandling.MERGE: {
        if (!newUrlSearchParams) {
          throw new InvalidOperationException(
            "In order to merge the query string keys you need to provide a value for the 'newUrlSearchParams'",
          );
        }

        const existingUrlSearchParams = new URLSearchParams(
          existingQueryString,
        );

        const mergedUrlSearchParams = this.mergeUrlSearchParams(
          existingUrlSearchParams,
          newUrlSearchParams,
        );

        return mergedUrlSearchParams;
      }
      case CustomQueryParamsHandling.REPLACE: {
        if (!newUrlSearchParams) {
          throw new InvalidOperationException(
            "In order to merge the query string keys you need to provide a value for the 'newUrlSearchParams'",
          );
        }

        return newUrlSearchParams;
      }
      default:
        throw new UnreachableCaseError(customQueryParamsHandling);
    }
  }

  private mergeUrlSearchParams(
    existingUrlSearchParams: URLSearchParams,
    newUrlSearchParams: URLSearchParams,
  ) {
    const mergedUrlSearchParams = new URLSearchParams();

    newUrlSearchParams.forEach((value, key) => {
      mergedUrlSearchParams.append(key, value);
    });

    existingUrlSearchParams.forEach((value, key) => {
      // This should prevent that the same key is being added multiple times
      // which 'append' does not prevent
      if (mergedUrlSearchParams.get(key) === null) {
        mergedUrlSearchParams.append(key, value);
      }
    });

    return mergedUrlSearchParams;
  }
}
